import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Logo from './Logo';

// Footer links organized by section
const footerLinks = {
  product: [
    { title: 'Features', path: '/features' },
    { title: 'Pricing', path: '/pricing' },
    { title: 'Security', path: '/security' },
  ],
  company: [
    { title: 'About Us', path: '/about' },
    { title: 'Careers', path: '/careers' },
    { title: 'Contact', path: '/contact' },
  ],
  resources: [
    { title: 'Blog', path: '/blog' },
    { title: 'Support', path: '/support' },
    { title: 'Documentation', path: '/documentation' },
  ],
  legal: [
    { title: 'Privacy Policy', path: '/privacy' },
    { title: 'Terms of Service', path: '/terms' },
    { title: 'Security Policy', path: '/security' },
  ],
};

// Social media links
const socialLinks = [
  { name: 'Facebook', icon: <FacebookIcon />, url: 'https://facebook.com' },
  { name: 'Twitter', icon: <TwitterIcon />, url: 'https://twitter.com' },
  { name: 'LinkedIn', icon: <LinkedInIcon />, url: 'https://linkedin.com' },
  { name: 'Instagram', icon: <InstagramIcon />, url: 'https://instagram.com' },
];

const Footer: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  return (
    <Box component="footer" sx={{ bgcolor: 'background.paper', py: 6, borderTop: 1, borderColor: 'divider' }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Logo variant="full" size="medium" />
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Superior Payroll provides modern payroll solutions for businesses of all sizes, helping you simplify the complexities of employee compensation.
            </Typography>
            <Box sx={{ mt: 2 }}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.name}
                  aria-label={social.name}
                  component="a"
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    mr: 1, 
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                    }
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Product
                </Typography>
                {footerLinks.product.map((link) => (
                  <Typography 
                    key={link.title} 
                    variant="body2" 
                    color="text.secondary" 
                    paragraph
                    component={RouterLink}
                    to={link.path}
                    sx={{ 
                      textDecoration: 'none',
                      display: 'block',
                      '&:hover': {
                        color: 'primary.main',
                      }
                    }}
                  >
                    {link.title}
                  </Typography>
                ))}
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Company
                </Typography>
                {footerLinks.company.map((link) => (
                  <Typography 
                    key={link.title} 
                    variant="body2" 
                    color="text.secondary" 
                    paragraph
                    component={RouterLink}
                    to={link.path}
                    sx={{ 
                      textDecoration: 'none',
                      display: 'block',
                      '&:hover': {
                        color: 'primary.main',
                      }
                    }}
                  >
                    {link.title}
                  </Typography>
                ))}
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Resources
                </Typography>
                {footerLinks.resources.map((link) => (
                  <Typography 
                    key={link.title} 
                    variant="body2" 
                    color="text.secondary" 
                    paragraph
                    component={RouterLink}
                    to={link.path}
                    sx={{ 
                      textDecoration: 'none',
                      display: 'block',
                      '&:hover': {
                        color: 'primary.main',
                      }
                    }}
                  >
                    {link.title}
                  </Typography>
                ))}
              </Grid>

              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Legal
                </Typography>
                {footerLinks.legal.map((link) => (
                  <Typography 
                    key={link.title} 
                    variant="body2" 
                    color="text.secondary" 
                    paragraph
                    component={RouterLink}
                    to={link.path}
                    sx={{ 
                      textDecoration: 'none',
                      display: 'block',
                      '&:hover': {
                        color: 'primary.main',
                      }
                    }}
                  >
                    {link.title}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'flex-start' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' } }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: { xs: 0, sm: 2 } }}>
              © {currentYear} Superior Payroll. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', mt: { xs: 1, sm: 0 }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' } }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                component={RouterLink}
                to="/privacy"
                sx={{ mx: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
              >
                Privacy
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                component={RouterLink}
                to="/terms"
                sx={{ mx: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 }, textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
              >
                Terms
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mx: { xs: 0, sm: 1 } }}
              >
                <Link href="mailto:support@superior-payroll.com" color="inherit" underline="hover">
                  Contact
                </Link>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: { xs: 2, sm: 0 } }}>
            {/* Empty box on the right side for balance */}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 